import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import TwoFaService from "../../service/twofa.service";

/**
 * Admin 2FA (Google Authenticator / TOTP) enrollment.
 * Opt-in: until enabled here, the admin logs in with password only. The otpauth:// URI is
 * rendered as a QR code in-browser (qrcode.react, no network call — the secret never leaves
 * our stack), with the Base32 key shown as a manual fallback ("Enter a setup key").
 */
export default function TwoFactor() {
  const [enabled, setEnabled] = useState(null); // null = loading
  const [setup, setSetup] = useState(null); // { secret, otpauth }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const loadStatus = async () => {
    try {
      setEnabled(await TwoFaService.status());
    } catch (e) {
      setErr("Couldn't load 2FA status.");
    }
  };
  useEffect(() => {
    loadStatus();
  }, []);

  const beginSetup = async () => {
    setErr("");
    setMsg("");
    setBusy(true);
    try {
      setSetup(await TwoFaService.setup());
    } catch (e) {
      setErr("Couldn't start setup.");
    } finally {
      setBusy(false);
    }
  };

  const confirmEnable = async () => {
    setErr("");
    setMsg("");
    setBusy(true);
    try {
      await TwoFaService.enable(code.trim());
      setSetup(null);
      setCode("");
      setMsg("Two-factor authentication is now ON.");
      await loadStatus();
    } catch (e) {
      setErr("That code didn't match. Try the current 6-digit code.");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setErr("");
    setMsg("");
    setBusy(true);
    try {
      await TwoFaService.disable(code.trim());
      setCode("");
      setMsg("Two-factor authentication is now OFF.");
      await loadStatus();
    } catch (e) {
      setErr("That code didn't match. Enter your current 6-digit code to turn 2FA off.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4">
      <h2 className="text-xl font-semibold mb-1">Two-factor authentication</h2>
      <p className="text-white-dark mb-4">
        Protect the admin login with Google Authenticator (or any TOTP app).
      </p>

      {msg && <div className="mb-3 text-green-600">{msg}</div>}
      {err && <div className="mb-3 text-red-500">{err}</div>}

      {enabled === null && <div className="text-white-dark">Loading…</div>}

      {enabled === true && (
        <div className="panel max-w-lg">
          <p className="mb-3">
            Status: <b className="text-green-600">ON</b>. You'll be asked for a 6-digit code at login.
          </p>
          <label className="font-semibold">Current code (to turn it off)</label>
          <input
            className="form-input mt-1 mb-3"
            inputMode="numeric"
            maxLength={6}
            placeholder="6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <button className="btn btn-danger" disabled={busy} onClick={disable}>
            {busy ? "…" : "Turn off 2FA"}
          </button>
        </div>
      )}

      {enabled === false && !setup && (
        <div className="panel max-w-lg">
          <p className="mb-3">
            Status: <b className="text-white-dark">OFF</b>. Add an authenticator to require a code at login.
          </p>
          <button className="btn btn-primary" disabled={busy} onClick={beginSetup}>
            {busy ? "…" : "Set up 2FA"}
          </button>
        </div>
      )}

      {enabled === false && setup && (
        <div className="panel max-w-lg">
          <ol className="list-decimal ms-5 space-y-2 mb-4">
            <li>
              In Google Authenticator tap <b>+ → Scan a QR code</b> and point it at this:
              {setup.otpauth && (
                <div className="mt-2 inline-block bg-white p-3 rounded">
                  <QRCodeSVG value={setup.otpauth} size={176} marginSize={0} />
                </div>
              )}
              <div className="text-white-dark text-sm mt-2">
                Can't scan? Tap <b>Enter a setup key</b> (account <b>heyPOP Admin</b>) and
                paste this key:
              </div>
              <div className="mt-1 font-mono text-lg break-all bg-gray-100 dark:bg-[#1b2e4b] p-2 rounded select-all">
                {setup.secret}
              </div>
            </li>
            <li>Enter the current 6-digit code it shows to confirm:</li>
          </ol>
          <input
            className="form-input mb-3"
            inputMode="numeric"
            maxLength={6}
            placeholder="6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <div className="flex gap-2">
            <button className="btn btn-primary" disabled={busy || code.length !== 6} onClick={confirmEnable}>
              {busy ? "…" : "Verify & enable"}
            </button>
            <button className="btn btn-outline-danger" disabled={busy} onClick={() => setSetup(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
